// homeTime.js
// Pure, timezone-stable date-formatting helpers for the Home redesign.
// All ISO strings are treated as UTC calendar dates (matching the rest of the
// app's `period.split("T")[0]` convention) so results are deterministic
// regardless of the machine's local timezone.
// See docs/superpowers/specs/2026-09-16-home-redesign.md §4/§5.

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const identity = (s) => s;

const toValidDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Localized name for a date part via Intl, or null when `lang` is absent/English
 * or Intl cannot serve it (unsupported locale, runtime without Intl). Callers
 * fall back to the English arrays on null.
 */
const intlPart = (date, lang, part) => {
  if (!lang || lang === "en") return null;
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") return null;
  try {
    const out = new Intl.DateTimeFormat(lang, { [part]: "short", timeZone: "UTC" }).format(date);
    return out || null;
  } catch (e) {
    return null;
  }
};

/**
 * Short weekday name ("Thu") for an ISO date, or "" when missing/invalid.
 * With a non-English `lang` it is the locale's own short name.
 */
export function weekdayShort(iso, lang) {
  const date = toValidDate(iso);
  if (!date) return "";
  return intlPart(date, lang, "weekday") || WEEKDAY_SHORT[date.getUTCDay()];
}

// Languages whose month-only Intl output reads unnaturally when concatenated
// ("金 18 10月"); format the whole date in one Intl call instead.
const WHOLE_DATE_LANGS = new Set(["ja", "zh", "ko"]);

const intlWholeDate = (date, lang) => {
  const base = typeof lang === "string" ? lang.toLowerCase().split(/[-_]/)[0] : "";
  if (!WHOLE_DATE_LANGS.has(base)) return null;
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") return null;
  try {
    const out = new Intl.DateTimeFormat(lang, {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(date);
    return out || null;
  } catch (e) {
    return null;
  }
};

/**
 * "{weekday} {day} {month}" (e.g. "Fri 18 Sep") for an ISO date, or the
 * translated "No due date" when the date is missing or invalid.
 */
export function formatDue(iso, t = identity, lang) {
  const date = toValidDate(iso);
  if (!date) return t("No due date");
  const whole = intlWholeDate(date, lang);
  if (whole) return whole;
  const month = intlPart(date, lang, "month") || MONTH_SHORT[date.getUTCMonth()];
  return `${weekdayShort(iso, lang)} ${date.getUTCDate()} ${month}`;
}

/**
 * "Today" / "Yesterday" / a full weekday name (within 6 days) / "{n} days ago".
 * `now` is a Date, passed in by the caller so the result is deterministic.
 */
export function relativeDay(iso, now, t = identity) {
  const date = toValidDate(iso);
  if (!date || !(now instanceof Date) || Number.isNaN(now.getTime())) return "";

  const dateOnly = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const nowOnly = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((nowOnly - dateOnly) / 86400000);

  if (diffDays <= 0) return t("Today");
  if (diffDays === 1) return t("Yesterday");
  if (diffDays <= 6) return t(WEEKDAY_LONG[date.getUTCDay()]);
  return t("{days} days ago", { days: diffDays });
}

/**
 * Time-spent stat for the "This week" card: plain minutes under an hour,
 * "{hours}h {minutes}m" from 60 minutes up. Returns { value, label }.
 */
export function formatWeekTime(minutes, t = identity) {
  const total = Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0;
  if (total < 60) return { value: total, label: t("Minutes") };
  return {
    value: t("{hours}h {minutes}m", { hours: Math.floor(total / 60), minutes: total % 60 }),
    label: t("Time"),
  };
}
